import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";

function pwStrengthError(pw) {
  if (!pw || pw.length < 8) return "At least 8 characters";
  if (!/[A-Za-z]/.test(pw)) return "Include at least one letter";
  if (!/\d/.test(pw)) return "Include at least one number";
  return "";
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const nav = useNavigate();

  const pwErr = useMemo(() => pwStrengthError(pw), [pw]);
  const mismatchErr = pw && pw2 && pw !== pw2 ? "Passwords do not match" : "";

  const submit = async (e) => {
    e.preventDefault();
    if (pwErr || mismatchErr) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/auth/reset-password", { token: token.trim(), new_password: pw });
      setDone(r.data?.username || "");
      setTimeout(() => nav("/login"), 1600);
    } catch (e2) {
      setErr(typeof e2?.response?.data?.detail === "string" ? e2.response.data.detail : "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <form onSubmit={submit} className="w-full max-w-md sc-card p-8" data-testid="reset-password-form">
        <div className="sc-label text-[#CCFF00]">Set new password</div>
        <h2 className="sc-heading text-3xl font-black mb-4 mt-1">Choose a strong password</h2>
        {done ? (
          <div className="space-y-3">
            <div className="text-sm text-zinc-300" data-testid="reset-password-success">
              Password updated for <span className="text-white">@{done}</span>. Redirecting to login...
            </div>
          </div>
        ) : (
          <>
            <label htmlFor="reset-token-input" className="sc-label block mb-2">Reset token</label>
            <input
              data-testid="reset-token"
              id="reset-token-input"
              name="reset_token"
              autoComplete="one-time-code"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="sc-input mb-4 font-mono text-xs"
              required
              placeholder="Paste the token from your email/logs"
            />
            <label htmlFor="reset-password-new-input" className="sc-label block mb-2">New password</label>
            <PasswordInput
              data-testid="reset-password-new"
              id="reset-password-new-input"
              name="new-password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="mb-1"
              required
            />
            <div className={`text-xs mb-3 ${pwErr ? "text-[#FF3366]" : "text-[#CCFF00]"}`} data-testid="reset-password-strength">
              {pwErr || "Strong password"}
            </div>
            <label htmlFor="reset-password-confirm-input" className="sc-label block mb-2">Confirm new password</label>
            <PasswordInput
              data-testid="reset-password-confirm"
              id="reset-password-confirm-input"
              name="new-password-confirm"
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="mb-1"
              required
            />
            {mismatchErr && <div className="text-[#FF3366] text-xs mb-3" data-testid="reset-mismatch">{mismatchErr}</div>}
            {err && <div className="text-[#FF3366] text-xs mb-3" data-testid="reset-error">{err}</div>}
            <button
              data-testid="reset-submit"
              disabled={busy || !!pwErr || !!mismatchErr || !token}
              className="sc-btn w-full mt-3 disabled:opacity-50"
            >
              {busy ? "Updating..." : "Update password"}
            </button>
            <div className="text-xs text-zinc-400 mt-4 text-center">
              <Link to="/login" className="text-[#00E5FF] hover:underline">Back to login</Link>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
